import { FC } from "react";

import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Button, Checkbox, InputNumber } from "antd";
import styled from "styled-components";

import useNormalizeFieldConversion from "hooks/useNormalizeFieldConversion";
import useUserUnit from "hooks/useUserUnit";

import { INormalizeBySetting } from "models";

import GroupBy from "components/groupBy";

import MultilinearNormalizeBySettings from "./MultilinearNormalizeBySettings";

type NormalizeSettingsT = {
  value: INormalizeBySetting;
  onChange: (v: INormalizeBySetting) => void;
};

const NormalizeBySettings: FC<NormalizeSettingsT> = ({ value, onChange }) => {
  const unitType = useUserUnit();

  // derived state
  const { display, field, per, unit, defaultUnit, useMultilinearNormalization, units } =
    value;

  const { displayLabel, displayUnit } = useNormalizeFieldConversion({
    field,
    fallbackDisplay: display,
    fallbackUnit: unit,
    units,
    defaultUnit
  });

  const groupByValue = {
    property: field,
    title: display
  };

  // handlers
  function handlePerChange(nextPer: number) {
    const nextValue = { ...value, per: nextPer > 0 ? nextPer : 1 };
    onChange(nextValue);
  }

  function handleMultilinearNormalizationChange(newValue: boolean) {
    const nextValue = { ...value, useMultilinearNormalization: newValue };
    onChange(nextValue);
  }

  function handleFieldChange(groupBy) {
    const { property, title, defaultUnit, normalizeByDefault, units } = groupBy;

    let convertedUnit = defaultUnit;
    if (units && units[unitType]) {
      convertedUnit = units[unitType];
    }

    const next: INormalizeBySetting = {
      display: title,
      field: property,
      per: normalizeByDefault > 0 ? normalizeByDefault : 1,
      unit: convertedUnit,
      defaultUnit: defaultUnit, // Keep original unit for backend and eva-unit-core conversion
      units: units,
      useMultilinearNormalization: false,
      threshold: 0,
      lowerScalar: 1,
      higherScalar: 1
    };

    onChange(next);
  }

  return (
    <>
      <Row>
        <LeftField>
          <Label>Normalize Field</Label>
          <GroupBy
            value={groupByValue}
            dataTypeFilters={["Number", "Integer"]}
            editable={false}
            placement="rightTop"
            onChange={handleFieldChange}>
            <StyledButton block>
              {displayUnit && !displayLabel.includes(`(${displayUnit})`)
                ? `${displayLabel} (${displayUnit})`
                : displayLabel}
              <KeyboardArrowRightIcon style={{ fontSize: 24 }} />
            </StyledButton>
          </GroupBy>
        </LeftField>

        <RightField>
          <Label>Per{displayUnit && ` (${displayUnit})`}</Label>
          <StyledNumberInput
            value={per}
            min={0}
            controls={false}
            onChange={handlePerChange}
          />
        </RightField>
      </Row>
      <Row>
        <Checkbox
          data-testid="normalize-multilinear-checkbox"
          checked={useMultilinearNormalization}
          onChange={(v) => handleMultilinearNormalizationChange(v.target.checked)}>
          Multilinear Normalization
        </Checkbox>
      </Row>
      {useMultilinearNormalization && (
        <MultilinearNormalizeBySettings value={value} onChange={onChange} />
      )}
    </>
  );
};

export default NormalizeBySettings;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5%;
  margin-bottom: 10px;
`;

const LeftField = styled.div`
  width: 62%;
`;

const RightField = styled.div`
  width: 33%;
`;

const Label = styled.span`
  white-space: break-spaces;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  margin-top: 0;
`;

const StyledButton = styled(Button)`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  height: 30px;
  text-align: left;

  & > span {
    max-width: fit-content;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledNumberInput = styled(InputNumber)`
  width: 100%;
  height: 30px;
`;
